import { ref, computed, reactive } from 'vue'
import { safeLocalStorage, isClient } from '@/composables/useSSR'

const STORAGE_KEY = 'devMode'
const ROLE_KEY = 'devModeRole'
const PANEL_POSITION_KEY = 'devPanelPosition'
const DEBUG_OVERLAY_KEY = 'devDebugOverlay'

// Only allow dev mode in development environment
const isDev = import.meta.env.DEV

// Shared reactive state - initialize with defaults, load from storage on client
const devModeEnabled = ref(false)
const simulatedRole = ref<'admin' | 'reseller' | 'customer' | null>(null)

// Panel and overlay state
const isPanelOpen = ref(false)
const panelPosition = ref<'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'>('bottom-right')
const isDebugOverlayVisible = ref(false)

// Load from localStorage on client
if (isClient) {
  const storage = safeLocalStorage()
  if (storage) {
    devModeEnabled.value = isDev && storage.getItem(STORAGE_KEY) === 'true'
    simulatedRole.value = (storage.getItem(ROLE_KEY) as 'admin' | 'reseller' | 'customer') || null
    panelPosition.value = (storage.getItem(PANEL_POSITION_KEY) as typeof panelPosition.value) || 'bottom-right'
    isDebugOverlayVisible.value = storage.getItem(DEBUG_OVERLAY_KEY) === 'true'
  }
}

// Performance metrics
const performanceMetrics = reactive({
  renderCount: 0,
  lastRenderTime: 0,
  avgRenderTime: 0,
  totalRenderTime: 0
})

export function useDevMode() {
  const isDevEnvironment = computed(() => isDev)

  const devMode = computed(() => isDev && devModeEnabled.value)

  const toggle = () => {
    if (!isDev) return
    devModeEnabled.value = !devModeEnabled.value
    const storage = safeLocalStorage()
    if (storage) {
      storage.setItem(STORAGE_KEY, String(devModeEnabled.value))
    }

    // Reset simulated role when disabling dev mode
    if (!devModeEnabled.value) {
      simulatedRole.value = null
      if (storage) storage.removeItem(ROLE_KEY)
      isPanelOpen.value = false
      isDebugOverlayVisible.value = false
    }
  }

  const setSimulatedRole = (role: 'admin' | 'reseller' | 'customer' | null) => {
    if (!isDev) return
    simulatedRole.value = role
    const storage = safeLocalStorage()
    if (storage) {
      if (role) {
        storage.setItem(ROLE_KEY, role)
      } else {
        storage.removeItem(ROLE_KEY)
      }
    }
  }

  // Get effective role (simulated or actual)
  const getEffectiveRole = (actualRole: string | undefined) => {
    if (devMode.value && simulatedRole.value) {
      return simulatedRole.value
    }
    return actualRole
  }

  // Panel controls
  const togglePanel = () => {
    if (!devMode.value) return
    isPanelOpen.value = !isPanelOpen.value
  }

  const closePanel = () => {
    isPanelOpen.value = false
  }

  const setPanelPosition = (position: typeof panelPosition.value) => {
    panelPosition.value = position
    const storage = safeLocalStorage()
    if (storage) storage.setItem(PANEL_POSITION_KEY, position)
  }

  // Debug overlay controls
  const toggleDebugOverlay = () => {
    if (!devMode.value) return
    isDebugOverlayVisible.value = !isDebugOverlayVisible.value
    const storage = safeLocalStorage()
    if (storage) storage.setItem(DEBUG_OVERLAY_KEY, String(isDebugOverlayVisible.value))
  }

  // Performance tracking
  const trackRender = (renderTime: number) => {
    performanceMetrics.renderCount++
    performanceMetrics.lastRenderTime = renderTime
    performanceMetrics.totalRenderTime += renderTime
    performanceMetrics.avgRenderTime = performanceMetrics.totalRenderTime / performanceMetrics.renderCount
  }

  const resetMetrics = () => {
    performanceMetrics.renderCount = 0
    performanceMetrics.lastRenderTime = 0
    performanceMetrics.avgRenderTime = 0
    performanceMetrics.totalRenderTime = 0
  }

  return {
    // Core dev mode
    isDevEnvironment,
    devMode,
    simulatedRole,
    toggle,
    setSimulatedRole,
    getEffectiveRole,
    // Panel
    isPanelOpen,
    panelPosition,
    togglePanel,
    closePanel,
    setPanelPosition,
    // Debug overlay
    isDebugOverlayVisible,
    toggleDebugOverlay,
    // Performance
    performanceMetrics,
    trackRender,
    resetMetrics
  }
}
